import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import { cn } from "design-system/utils"
import { formatPercent } from "lopermut"
import { useTranslation } from "react-i18next"
import { Cell, Label, Pie, PieChart } from "recharts"
import { IssueUrgencyEnum } from "schema"

const chartConfig = {} satisfies ChartConfig

const URGENCY_COLORS = {
  [IssueUrgencyEnum.High]: "var(--support-primary)",
  [IssueUrgencyEnum.Medium]: "var(--support-secondary)",
  [IssueUrgencyEnum.Low]: "var(--neutral)",
} as const

type Props = {
  highRate: number | null | undefined
  highAndMediumRate?: number | null
  mini?: boolean
}

function clampRate(value: number) {
  return Math.max(0, Math.min(1, value))
}

export function UrgencyScore({ highRate, highAndMediumRate, mini }: Props) {
  const { t } = useTranslation()
  const noData = highRate == null
  const high = clampRate(highRate ?? 0)
  const nested = clampRate(Math.max(high, highAndMediumRate ?? high))
  const segments = [
    {
      key: IssueUrgencyEnum.High,
      value: high,
      color: URGENCY_COLORS[IssueUrgencyEnum.High],
    },
    {
      key: IssueUrgencyEnum.Medium,
      value: nested - high,
      color: URGENCY_COLORS[IssueUrgencyEnum.Medium],
    },
    {
      key: IssueUrgencyEnum.Low,
      value: 1 - nested,
      color: URGENCY_COLORS[IssueUrgencyEnum.Low],
    },
  ]
  const pieData = segments.filter((segment) => segment.value > 0)
  const chartData =
    pieData.length > 0 ? pieData : [{ key: "empty", value: 1, color: "var(--muted)" }]

  return (
    <div className="flex flex-row items-center gap-4">
      <ChartContainer
        config={chartConfig}
        className={cn(
          "aspect-square",
          mini ? "h-[14px]" : "h-[100px]",
          noData && "pointer-events-none grayscale opacity-25 dark:opacity-5",
        )}
      >
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            innerRadius={mini ? 5 : 40}
            outerRadius={mini ? 7 : 50}
            cornerRadius={0}
            animationDuration={0}
          >
            {chartData.map((segment) => (
              <Cell key={segment.key} fill={segment.color} />
            ))}
            {!mini && (
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-[10px] fill-muted-foreground"
                      >
                        {t("shared.rates.urgency")}
                      </text>
                    )
                  }
                }}
              />
            )}
          </Pie>
        </PieChart>
      </ChartContainer>
      {!mini && !noData && (
        <div className="flex flex-col gap-1">
          {segments.map((segment) => (
            <div key={segment.key} className="flex items-center gap-1.5">
              <div className="size-2 rounded-full shrink-0" style={{ background: segment.color }} />
              <span className="text-xs text-muted-foreground">
                {t(`support.entities.urgency.${segment.key}`)}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatPercent({ amount: segment.value }, true)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
